/* eslint-disable no-nested-ternary */
import styled, { css, keyframes } from "styled-components";
import { SAVINGS_ICON_DIMENSION, TRAILING_ICON_HEIGHT } from "../CartSavingsBannerOneLineV2";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { SAVINGS_BANNER_ANIMATION_DURATION } from "../constants";

interface ContainerProps {
    isBolt?: boolean;
    is99Store?: boolean;
    isEOCart?: boolean;
    savingsBannerGradient?: string;
    savingsShimmerGradient?: string;
    shouldPlayAnimation?: boolean;
}

interface TrailingIconWrapperProps {
    width?: string;
    height?: string;
}

const getStripGradient = (
    isBolt: boolean | undefined,
    is99Store: boolean | undefined,
    isEOCart: boolean | undefined,
    savingsBannerGradient: string | undefined,
) => {
    // Use backend-provided gradient if available
    if (savingsBannerGradient) {
        return savingsBannerGradient;
    }

    // Fallback to frontend gradients
    if (is99Store) {
        return "linear-gradient(90deg, #82C5F8 0.92%, rgba(130, 197, 248, 0.00) 79.88%)";
    }
    if (isEOCart) {
        return "linear-gradient(90deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.00) 100%)";
    }
    if (isBolt) {
        return "linear-gradient(90deg, #801945 0%, rgba(112, 26, 51, 0) 71.58%)";
    }
    return "linear-gradient(90deg, #441439 4.33%, rgba(134, 55, 121, 0.4) 45.49%, rgba(134, 55, 121, 0.00) 100%)";
};

export const SavingsBannerOnelineContainer = styled.div<{ isGiftingCartFlow?: boolean }>(
    ({ theme, isGiftingCartFlow = false }) => ({
        background: "#ddfbef",
        borderRadius: isGiftingCartFlow ? "0px" : `7px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: isGiftingCartFlow ? "0" : `0 ${theme.spacing.medium}`,
        padding: `${theme.spacing.xxSmall} ${theme.spacing.medium}`,
        border: isGiftingCartFlow ? "none" : "1px solid var(--container-border-tertiary-black, rgba(2, 6, 12, 0.10))",
    }),
);

export const IconWrapper = styled.div<{ isGiftingCartFlow?: boolean }>(({ theme, isGiftingCartFlow = false }) => {
    return {
        width: SAVINGS_ICON_DIMENSION,
        height: SAVINGS_ICON_DIMENSION,
        marginRight: theme.spacing.xxxSmall,
        marginBottom: theme.spacing.xxxSmall,
        marginLeft: isGiftingCartFlow ? theme.spacing.xLarge : "0",
    };
});

export const TrailingIconWrapper = styled.div<TrailingIconWrapperProps>(({ theme, width }) => {
    return {
        width: width || "auto",
        height: TRAILING_ICON_HEIGHT,
        marginRight: theme.spacing.xxxSmall,
        marginLeft: theme.spacing.xxxSmall,
    };
});

export const SavingsBannerOnelineBlackContainer = styled.div<ContainerProps>(
    ({ theme, isBolt, is99Store, isEOCart, savingsBannerGradient, savingsShimmerGradient, shouldPlayAnimation }) => css`
        border-radius: ${theme.spacing.xSmall};
        display: flex;
        height: 29px;
        align-items: center;
        justify-content: flex-start;
        margin-left: ${theme.spacing.medium};
        padding: ${theme.spacing.xSmall} ${theme.spacing.small};
        padding-right: 0;
        background: ${getStripGradient(isBolt, is99Store, isEOCart, savingsBannerGradient)};
        position: relative;
        overflow: hidden;

        ${shouldPlayAnimation &&
        savingsBannerGradient &&
        css`
            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                width: 250px;
                height: 100%;
                opacity: 1;
                background: linear-gradient(
                    90deg,
                    rgba(182, 223, 255, 0) 0%,
                    #c9e7ff 25%,
                    #dcf0ff 50%,
                    #bfe3ff 75%,
                    rgba(182, 223, 255, 0) 100%
                );
                transform: translateX(-100%);
                mix-blend-mode: normal;
                animation: ${savingsBannerShimmer} ${SAVINGS_BANNER_ANIMATION_DURATION}ms ease-in-out forwards;

                @supports (mix-blend-mode: plus-lighter) {
                    mix-blend-mode: plus-lighter;
                    opacity: 0.7;
                    filter: blur(23.3px);
                    width: 160px;
                    background: ${savingsShimmerGradient};
                    animation: ${savingsBannerShimmer} ${SAVINGS_BANNER_ANIMATION_DURATION}ms ease-in-out forwards;
                }
            }
        `}
    `,
);

// 3s
const flipExpressAnimation = keyframes`
  0% {
    transform: perspective(400px) translateX(70%);
  }
  60% {
    transform: perspective(400px) translateX(0%);
  }
  70% {
    transform: perspective(400px) translateX(0%) rotateX(180deg);
  }
  100% {
    transform: perspective(400px) translateX(0%) rotateX(0deg);
  }
`;

const translateAnimation = keyframes`
  0% {
    transform: perspective(400px) translateX(100%);
  }
  100% {
    transform: perspective(400px) translateX(0%);
  }
`;

const savingsBannerShimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(calc(250%));
  }
`;

export const FlipText = styled.div<{
    time?: string;
    showFlipAnimation?: boolean;
    showFinalTheme?: boolean;
    flip?: boolean;
    isBolt?: boolean;
}>(
    ({ time, theme, showFlipAnimation, showFinalTheme, flip, isBolt }) => css`
        display: flex;
        align-items: baseline;
        margin-left: ${theme.spacing.xxxSmall};
        transform: ${flip || showFinalTheme ? "none" : "perspective(400px) translateX(70%)"}; // Initial transform state
        animation: ${showFinalTheme ? "none" : showFlipAnimation && !isBolt ? flipExpressAnimation : translateAnimation}
            ${time || "2s"} 1 ${showFlipAnimation ? "0.5s" : "0s"} forwards; // Add 1s delay for flipExpressAnimation
        transform-origin: center right;
        backface-visibility: hidden;
        position: relative;
        overflow: hidden;
    `,
);

export const GiftingVerticalLineContainer = styled.div`
    padding: 12px;
    position: absolute;
    background: linear-gradient(
        180deg,
        rgba(219, 255, 241, 0) 14.78%,
        rgba(193, 255, 230, 0.81) 56.93%,
        #ddfbef 115.32%
    );
    top: 0;
    bottom: 0;
    right: 32px;
`;

export const GiftingBowTieLineContainer = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;

export const GiftingBowTieImageWrapper = styled.div`
    position: relative;
    top: 30px;
    @supports (top: var(--sat)) {
        top: calc(var(--sat) + 30px);
    }
    left: -24px;
    width: 72px;
    height: 72px;
`;

export const GiftingBowTieLineImage = styled(Image)(() => {
    return {
        width: "100%",
        height: "100%",
    };
});
